import React, { useEffect, useMemo, useState } from 'react';

import { Web } from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CASTable from './CASTable';
import getCSVData from './csv';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import { getList, userHasAccess } from '../operations';
import CASForm from './CASForm';
import getEdgeReportingMenu from '../getEdgeReportingMenu';

import './styles.scoped.css';

const ContractActivitySearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const [data, setData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [LOB, setLOB] = useState([]);
  const [CAS, setCAS] = useState([]);
  const [form, setForm] = useState({});
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingLOB, setIsLoadingLOB] = useState(false);
  const [isLoadingCAS, setIsLoadingCAS] = useState(true);

  useEffect(() => {
    if (menuItems) {
      // eslint-disable-next-line no-undef
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  const handleGetLOB = async () => {
    setIsLoadingLOB(true);
    const lob = await getList('line-of-business');
    setLOB(lob);
    setIsLoadingLOB(false);
  };

  const handleGetCAS = async () => {
    setIsLoadingCAS(true);
    const cas = await getList('contract-activity-status');
    setCAS(cas);
    setIsLoadingCAS(false);
  };

  const handleGetBranches = async () => {
    setIsLoadingBranches(true);
    const newBranches = await getList('clients/branches');
    setBranches(newBranches);
    setIsLoadingBranches(false);
  };

  useEffect(() => {
    handleGetCAS();
    handleGetLOB();
    handleGetBranches();
  }, []);

  const casOptions = useMemo(
    () =>
      CAS.map(({ REF_CODE, REF_DESCRIPTION }) => ({
        label: REF_DESCRIPTION,
        value: REF_CODE,
      })),
    [CAS],
  );

  const lobOptions = useMemo(
    () =>
      LOB.map(({ LineOfBusinessCode, LineOfBusinessDesc }) => ({
        label: LineOfBusinessDesc,
        value: LineOfBusinessCode,
      })),
    [LOB],
  );

  const branchOptions = useMemo(
    () =>
      branches?.map(({ Branch_Code, Branch_Name }) => ({
        label: Branch_Name,
        value: Branch_Code,
      })),
    [branches],
  );

  const fileName = `edge-reporting-activity-search-${newDateFormatted.toString()}.csv`;

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Contract Activity Search report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Contract Activity Search'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'Viewing Report' : 'Contract Activity Search'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <CSVLink filename={fileName} data={getCSVData(data)}>
            <ImageColorChanger
              src={Excel}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />
          </CSVLink>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <CASTable data={data} loading={loadingResults} />
          <div className="buttons-row">
            <Button variant="primary" onPress={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />

            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <CASForm
              form={form}
              setForm={setForm}
              isLoadingCAS={isLoadingCAS}
              isLoadingLOB={isLoadingLOB}
              isLoadingBranches={isLoadingBranches}
              casOptions={casOptions}
              lobOptions={lobOptions}
              branchOptions={branchOptions}
              setLoadingResults={setLoadingResults}
              setData={setData}
              setShowResults={setShowResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default ContractActivitySearch;
